import { keepPreviousData, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { toast } from '../toast';
import type { NotificationResponse, SendCustomNotificationRequest, SendBatchNotificationRequest, RegisterPushTokenRequest } from '@sportschool/shared-types';

export function useNotifications(schoolId: string) {
  return useQuery<NotificationResponse[]>({
    queryKey: ['notifications', schoolId],
    queryFn: async () => {
      const res = await api.get(`/schools/${schoolId}/notifications`);
      return res.data;
    },
    enabled: !!schoolId,
  });
}

export function useMarkNotificationRead(schoolId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.post(`/schools/${schoolId}/notifications/${id}/read`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications', schoolId] });
      toast.success('Marked as read');
    },
    onError: () => toast.error('Failed to mark as read'),
  });
}

export function useMarkAllNotificationsRead(schoolId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await api.post(`/schools/${schoolId}/notifications/read-all`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications', schoolId] });
      toast.success('All marked as read');
    },
    onError: () => toast.error('Failed to mark all as read'),
  });
}

/** One page of a member's notifications, newest first (the API has no total count). */
export function useMemberNotifications(
  schoolId: string,
  memberId: string,
  { page = 1, pageSize = 50, enabled = true }: { page?: number; pageSize?: number; enabled?: boolean } = {},
) {
  return useQuery<NotificationResponse[]>({
    queryKey: ['member-notifications', schoolId, memberId, page, pageSize],
    queryFn: async () => {
      const res = await api.get(`/schools/${schoolId}/members/${memberId}/notifications`, { params: { page, pageSize } });
      return res.data;
    },
    enabled: enabled && !!schoolId && !!memberId,
    placeholderData: keepPreviousData,
  });
}

export function useSendCustomNotificationToMember(schoolId: string, memberId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: SendCustomNotificationRequest) => {
      await api.post(`/schools/${schoolId}/members/${memberId}/notifications`, data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['member-notifications', schoolId, memberId] });
      toast.success('Powiadomienie zostało wysłane');
    },
    onError: (err: any) => {
      const message = err.response?.data?.detail || err.message || 'Nie udało się wysłać powiadomienia';
      toast.error(message);
    },
  });
}

export function useSendBatchNotification(schoolId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: SendBatchNotificationRequest) => {
      await api.post(`/schools/${schoolId}/notifications/send-custom`, data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications', schoolId] });
      qc.invalidateQueries({ queryKey: ['member-notifications', schoolId] });
      toast.success('Powiadomienie zostało wysłane');
    },
    onError: (err: any) => {
      const message = err.response?.data?.detail || err.message || 'Nie udało się wysłać powiadomienia';
      toast.error(message);
    },
  });
}

export function useRegisterPushToken(schoolId?: string) {
  return useMutation({
    mutationFn: async (data: RegisterPushTokenRequest) => {
      const url = schoolId ? `/schools/${schoolId}/notifications/push-tokens` : '/notifications/push-tokens';
      const res = await api.post(url, data);
      return res.data;
    },
  });
}

export function useUnregisterPushToken(schoolId?: string) {
  return useMutation({
    mutationFn: async (token: string) => {
      const url = schoolId
        ? `/schools/${schoolId}/notifications/push-tokens/${encodeURIComponent(token)}`
        : `/notifications/push-tokens/${encodeURIComponent(token)}`;
      await api.delete(url);
    },
  });
}

